<template>
  <div class="page-edit-third-party">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li>
          <router-link :to="{ name: 'DashboardView' }">Dashboard</router-link>
        </li>
        <li>
          <router-link :to="{ name: 'DashboardThirdParties' }"
            >Third Parties</router-link
          >
        </li>
        <li>
          <router-link
            :to="{ name: 'ThirdPartyView', params: { id: thirdParty.id } }"
          >
            {{ thirdParty.name }}
          </router-link>
        </li>
        <li class="is-active">
          <a>Edit</a>
        </li>
      </ul>
    </nav>

    <h1 class="title">Edit – {{ thirdParty.name }}</h1>

    <!-- Notifications -->
    <div
      v-if="notification.message"
      class="notification"
      :class="notification.type"
    >
      {{ notification.message }}
      <button class="delete" @click="notification.message = ''"></button>
    </div>

    <div class="box">
      <div class="columns">
        <!-- Left column -->
        <div class="column is-6">
          <div class="field">
            <label class="label">Name</label>
            <input type="text" class="input" v-model="thirdParty.name" />
          </div>
          <div class="field">
            <label class="label">Email</label>
            <input type="email" class="input" v-model="thirdParty.email" />
          </div>
          <div class="field">
            <label class="label">Org Number</label>
            <input type="text" class="input" v-model="thirdParty.org_number" />
          </div>
          <div class="field">
            <label class="label">Contact Person</label>
            <input
              type="text"
              class="input"
              v-model="thirdParty.contact_person"
            />
          </div>
          <div class="field">
            <label class="label">Contact Reference</label>
            <input
              type="text"
              class="input"
              v-model="thirdParty.contact_reference"
            />
          </div>
        </div>

        <!-- Right column -->
        <div class="column is-6">
          <div class="field">
            <label class="label">Address 1</label>
            <input type="text" class="input" v-model="thirdParty.address1" />
          </div>
          <div class="field">
            <label class="label">Address 2</label>
            <input type="text" class="input" v-model="thirdParty.address2" />
          </div>
          <div class="field">
            <label class="label">Zipcode</label>
            <input type="text" class="input" v-model="thirdParty.zipcode" />
          </div>
          <div class="field">
            <label class="label">Place</label>
            <input type="text" class="input" v-model="thirdParty.place" />
          </div>
          <div class="field">
            <label class="label">Region</label>
            <input type="text" class="input" v-model="thirdParty.region" />
          </div>
          <div class="field">
            <label class="label">Country</label>
            <input type="text" class="input" v-model="thirdParty.country" />
          </div>
        </div>
      </div>
      <!-- Legal -->
      <div class="columns">
        <div class="column is-6">
          <div class="field">
            <label class="label">Legal name</label>
            <input type="text" class="input" v-model="thirdParty.legal_name" />
          </div>
        </div>
        <div class="column is-3">
          <div class="field">
            <label class="label">VAT number</label>
            <input type="text" class="input" v-model="thirdParty.vat_number" />
          </div>
        </div>
        <div class="column is-3">
          <div class="field">
            <label class="label">Country code</label>
            <input
              type="text"
              class="input"
              v-model="thirdParty.country_code"
              placeholder="BE"
            />
          </div>
        </div>
      </div>

      <!-- Peppol section -->
      <h2 class="subtitle mt-4">Peppol</h2>
      <div class="columns">
        <div class="column is-6">
          <div class="field">
            <label class="label">Endpoint ID</label>
            <input
              type="text"
              class="input"
              v-model="thirdParty.peppol_endpoint_id"
            />
          </div>
        </div>
        <div class="column is-6">
          <div class="field">
            <label class="label">Scheme</label>
            <input
              type="text"
              class="input"
              v-model="thirdParty.peppol_scheme_id"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="buttons is-right">
      <router-link
        :to="{ name: 'ThirdPartyView', params: { id: thirdParty.id } }"
        class="button is-light"
      >
        Cancel
      </router-link>
      <button
        type="button"
        class="button is-success"
        @click="submitForm"
        :disabled="loading"
      >
        <span v-if="loading" class="loader"></span>
        <span v-else>Save changes</span>
      </button>
    </div>
  </div>
</template>

<script>
import api from '@/utils/axios';

export default {
  name: 'EditThirdParty',
  data() {
    return {
      thirdParty: {},
      notification: { message: '', type: 'notification is-success' },
      loading: false,
    };
  },
  mounted() {
    this.getThirdParty();
  },
  methods: {
    async getThirdParty() {
      const id = this.$route.params.id;
      this.loading = true;
      try {
        const { data } = await api.get(`/third-parties/${id}/`);
        // Ensure reactive defaults for new Peppol/legal fields
        this.thirdParty = {
          ...data,
          legal_name: data.legal_name ?? '',
          vat_number: data.vat_number ?? '',
          country_code: (data.country_code ?? 'BE').toUpperCase(),
          peppol_endpoint_id: data.peppol_endpoint_id ?? '',
          peppol_scheme_id: data.peppol_scheme_id ?? '9930',
        };
      } catch (e) {
        this.notify('Error loading third party', 'notification is-danger');
      } finally {
        this.loading = false;
      }
    },

    // Convert empty strings/undefined to null (so PATCH can clear values)
    nullifyEmpty(v) {
      return v === '' || v === undefined ? null : v;
    },

    // Small UX hint if endpoint/scheme mismatch
    warnIfPeppolIncomplete(payload) {
      const ep = payload.peppol_endpoint_id;
      const sc = payload.peppol_scheme_id;
      if ((ep && !sc) || (!ep && sc)) {
        this.notify(
          'Peppol: provide both Endpoint and Scheme for best compatibility.',
          'notification is-warning'
        );
      }
    },

    async submitForm() {
      const id = this.$route.params.id;
      this.loading = true;

      try {
        // Clone reactive object to a plain object (avoid proxies)
        const cleanObject = JSON.parse(JSON.stringify(this.thirdParty));

        // Prepare only editable fields + normalize
        const payload = {
          name: this.nullifyEmpty(cleanObject.name),
          email: this.nullifyEmpty(cleanObject.email),
          org_number: this.nullifyEmpty(cleanObject.org_number),
          address1: this.nullifyEmpty(cleanObject.address1),
          address2: this.nullifyEmpty(cleanObject.address2),
          zipcode: this.nullifyEmpty(cleanObject.zipcode),
          place: this.nullifyEmpty(cleanObject.place),
          country: this.nullifyEmpty(cleanObject.country),
          region: this.nullifyEmpty(cleanObject.region),
          contact_person: this.nullifyEmpty(cleanObject.contact_person),
          contact_reference: this.nullifyEmpty(cleanObject.contact_reference),

          // ---- Legal / Peppol additions ----
          legal_name: this.nullifyEmpty(cleanObject.legal_name),
          vat_number: this.nullifyEmpty(cleanObject.vat_number),
          country_code: (cleanObject.country_code || 'BE').toUpperCase(),

          peppol_endpoint_id: this.nullifyEmpty(cleanObject.peppol_endpoint_id),
          peppol_scheme_id: this.nullifyEmpty(
            cleanObject.peppol_scheme_id || '9930'
          ),
        };

        this.warnIfPeppolIncomplete(payload);

        await api.patch(`/third-parties/${id}/`, payload);
        this.notify('Saved successfully', 'notification is-success');
        this.$router.push({ name: 'ThirdPartyView', params: { id } });
      } catch (err) {
        console.error('Erreur PATCH:', err);
        this.notify('Error saving third party', 'notification is-danger');
      } finally {
        this.loading = false;
      }
    },

    notify(message, type) {
      // type should include the "notification" class (Bulma)
      this.notification.message = message;
      this.notification.type = type || 'notification is-info';
    },
  },
};
</script>

<style scoped>
.loader {
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  animation: spin 1s linear infinite;
  display: inline-block;
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
